import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  type AssetModelBillingAnomalyModel,
  type InAppNotificationReadModel,
  type InAppNotificationType,
} from "@doitintl/cmp-models";
import { type ModelReference, type QueryDocumentSnapshotModel, type QuerySnapshotModel } from "@doitintl/models-any";
import { ModelTimestamp } from "@doitintl/models-any";
import { DateTime } from "luxon";

import { useFirestoreIdealHookOnce } from "../../Components/hooks/useFirestoreIdeal";
import { consoleErrorWithSentry } from "../../utils";
import { useAuthContext } from "../AuthContext";
import { useCustomerContext } from "../CustomerContext";
import { useUserContext } from "../UserContext";
import { Alert } from "./mappers/alert";
import { Budget } from "./mappers/budget";
import { CostAnomaly } from "./mappers/costAnomaly";
import { InvoiceReminder } from "./mappers/invoiceReminder";
import { KnownIssues } from "./mappers/knownIssues";
import { NewInvoice } from "./mappers/newInvoice";
import { ReportReady } from "./mappers/reportReady";
import { type InAppNotification, type Mapper, maxDays, type RealTimeNotification } from "./types";

const enabledNotifications: Mapper[] = [
  new Budget(),
  new CostAnomaly(),
  new NewInvoice(),
  new InvoiceReminder(),
  new Alert(),
  new KnownIssues(),
  new ReportReady(),
];

export type InAppNotifications = {
  notifications: Map<string, InAppNotification>;
  setNotifications: Dispatch<SetStateAction<Map<string, InAppNotification>>>;
  userRead?: Map<string, InAppNotificationReadModel>;
  loading: boolean;
  realTimeQueue: RealTimeNotification[];
  setRealTimeQueue: Dispatch<SetStateAction<RealTimeNotification[]>>;
  showDemoCostAnomaly: () => void;
};

const InAppNotificationsContext = createContext<InAppNotifications>({
  notifications: new Map<string, InAppNotification>(),
  loading: true,
  setNotifications: () => {},
  realTimeQueue: [],
  setRealTimeQueue: () => {},
  showDemoCostAnomaly: () => {},
});

export const InAppNotificationsContextProvider = ({ children }: { children?: ReactNode }) => {
  const { customer } = useCustomerContext({ allowNull: true });
  const { userRoles, userModel } = useUserContext({ requiredRoles: true });
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });

  const [loading, setLoading] = useState(true);
  const [loadedStatus, setLoadedStatus] = useState<Map<InAppNotificationType, boolean>>(new Map());
  const [notifications, setNotifications] = useState<Map<string, InAppNotification>>(new Map());
  const [userRead, setUserRead] = useState<Map<string, InAppNotificationReadModel> | undefined>();
  const [realTimeQueue, setRealTimeQueue] = useState<RealTimeNotification[]>([]);
  const prevNotifications = useRef<Map<string, InAppNotification>>(new Map());
  const [idealFirestore, setIdealFirestore] = useState(false);

  const maxPerType = 10;

  const showDemoCostAnomaly = () => {
    if (!customer?.id) return;

    const demoId = `demo-${Date.now()}`;
    const now = ModelTimestamp.now();

    // Create a mock document snapshot with demo data
    const mockDocData = {
      customer: {
        id: customer.id,
        path: `customers/${customer.id}`,
      },
      timestamp: now,
      metadata: {
        timestamp: now,
        platform: "google-cloud",
        project_id: "demo-project-123",
        service_name: "Compute Engine",
        type: "COST",
        excess: 1234.56,
        unit: "USD",
        severity: 2,
        alert_id: demoId,
        billing_account_id: "demo-billing-123",
      },
      chart_data: {
        "2024-03": {
          high: 1234.56,
          low: 0,
          snapshot_value: 1234.56,
        },
      },
    } as const;

    const mockDocSnap = {
      id: demoId,
      modelRef: undefined as unknown as ModelReference<AssetModelBillingAnomalyModel>,
      asModelData: () => mockDocData as unknown as AssetModelBillingAnomalyModel,
      ref: {
        parentModel: () => ({
          parentModel: () => ({
            id: "demo-asset",
          }),
        }),
      },
    } as const;

    // Use the CostAnomaly mapper to create the notification
    const costAnomalyMapper = new CostAnomaly();
    const demoNotification = costAnomalyMapper.toNotification(
      mockDocSnap as QueryDocumentSnapshotModel<AssetModelBillingAnomalyModel>
    );

    if (demoNotification) {
      setRealTimeQueue((prev) => [
        ...prev,
        {
          ...demoNotification,
          newOrUpdated: "New",
        },
      ]);
    }
  };

  useFirestoreIdealHookOnce("inAppNotifications", 5000, () => {
    setIdealFirestore(true);
  });

  // clear notifications on customer change
  useEffect(() => {
    setNotifications(new Map());
    setRealTimeQueue([]);
    setLoading(true);
    setLoadedStatus(new Map());
    prevNotifications.current = new Map();
  }, [customer?.ref]);

  // load user read status
  useEffect(() => {
    if (!userModel.ref || !customer?.ref || !idealFirestore) {
      return;
    }

    const onSnapshot = (docSnapshot: QuerySnapshotModel<InAppNotificationReadModel>) => {
      const readStatus = new Map<string, InAppNotificationReadModel>();
      if (docSnapshot.empty) {
        setUserRead(readStatus);
        return;
      }
      docSnapshot.forEach((doc) => {
        readStatus.set(doc.id, doc.data());
      });
      setUserRead(readStatus);
    };

    const colRef = userModel.ref.collection("inAppNotificationRead");

    if (isDoitEmployee) {
      return colRef
        .where("customer", "==", customer?.ref) // Doers may have multiple customers
        .onSnapshot(onSnapshot);
    } else {
      return colRef.onSnapshot(onSnapshot);
    }
  }, [idealFirestore, userModel.ref, customer?.ref, isDoitEmployee]);

  const userWantsNotificationType = useCallback<(notificationType: Mapper) => boolean>(
    (notificationType: Mapper) => {
      const types = userModel.inAppNotificationSettings?.notificationTypes;
      if (!types) {
        // user has no notification settings; assume they want all notifications because the settings haven't been configured yet.
        return true;
      }

      return Boolean(types.includes(notificationType.type));
    },
    [userModel.inAppNotificationSettings?.notificationTypes]
  );

  // load notifications, add listener for real-time updates, and set loading status
  useEffect(() => {
    if (!customer?.ref || !idealFirestore) {
      return;
    }

    const startTime = DateTime.utc().minus({ days: maxDays }).toJSDate();
    const setLoadingStatusForType = (type: InAppNotificationType, status: boolean) => {
      setLoadedStatus((prev) => {
        const newLoadedStatus = new Map(prev);
        newLoadedStatus.set(type, status);
        return newLoadedStatus;
      });
    };

    const subscribe = (notificationMapper: Mapper) => {
      const userHasPermission = isDoitEmployee || notificationMapper.userHasPermission(userRoles);
      if (!userHasPermission || !userWantsNotificationType(notificationMapper)) {
        setLoadingStatusForType(notificationMapper.type, false);
        return;
      }

      const col = notificationMapper
        .getCollection(customer.ref, startTime, userModel.email, isDoitEmployee)
        .limit(maxPerType);
      return col.onSnapshot(
        async (docSnapshot) => {
          let loadSuccess = false;
          try {
            const changes = docSnapshot.docChanges();
            const notifications: (InAppNotification | null)[] = await Promise.all(
              changes.map(async (change) => {
                // ignore removed
                if (change.type === "removed") {
                  return null;
                }
                return notificationMapper.toNotification(change.doc);
              })
            );

            setNotifications((prev) => {
              const newNotifications = new Map(prev);
              notifications.forEach((n) => {
                if (n) {
                  newNotifications.set(n.id, n);
                }
              });
              return newNotifications;
            });
            loadSuccess = true;
          } catch (e) {
            consoleErrorWithSentry(e, {
              message: `InAppNotifications: loading notifications ${notificationMapper.type}`,
            });
          }
          setLoadingStatusForType(notificationMapper.type, loadSuccess);
        },
        async (e) => {
          setLoadingStatusForType(notificationMapper.type, false);
          consoleErrorWithSentry(e, {
            message: `InAppNotifications: loading notifications ${notificationMapper.type}`,
          });
        }
      );
    };
    const unSubs = enabledNotifications
      .map((notificationType) => subscribe(notificationType))
      .filter((sub): sub is () => void => sub !== undefined);

    return () => {
      unSubs.forEach((unSub) => {
        unSub();
      });
    };
  }, [idealFirestore, customer?.ref, isDoitEmployee, userModel.email, userRoles, userWantsNotificationType]);

  useEffect(() => {
    if (loadedStatus.size === enabledNotifications.length) {
      setLoading(false);
    }
  }, [loadedStatus]);

  // add new or updated notifications to real-time queue
  useEffect(() => {
    const allowRealTime = !userModel.inAppNotificationSettings || userModel.inAppNotificationSettings.allowRealTime;

    // ignore initial load or if the user has disabled real-time notifications
    if (loading || !allowRealTime) {
      return;
    }
    if (prevNotifications.current.size !== 0) {
      notifications.forEach((n) => {
        const prev = prevNotifications.current.get(n.id);
        if (!prev) {
          setRealTimeQueue((prev) => [
            ...prev,
            {
              ...n,
              newOrUpdated: "New",
            },
          ]);
        } else if (prev.updated?.seconds !== n.updated?.seconds) {
          setRealTimeQueue((prev) => [
            ...prev,
            {
              ...n,
              newOrUpdated: "Updated",
            },
          ]);
        }
      });
    }

    prevNotifications.current = notifications;
  }, [notifications, loading, userModel.inAppNotificationSettings]);

  return (
    <InAppNotificationsContext.Provider
      value={{
        notifications,
        userRead,
        setNotifications,
        loading,
        realTimeQueue,
        setRealTimeQueue,
        showDemoCostAnomaly,
      }}
    >
      {children}
    </InAppNotificationsContext.Provider>
  );
};

export const useInAppNotifications = (): InAppNotifications => useContext(InAppNotificationsContext);

// Add testing provider
export const InAppNotificationsContextProviderForTesting = ({
  value,
  children,
}: {
  value?: Partial<InAppNotifications>;
  children: ReactNode;
}) => {
  const defaultValue = useContext(InAppNotificationsContext);
  return (
    <InAppNotificationsContext.Provider value={{ ...defaultValue, ...value }}>
      {children}
    </InAppNotificationsContext.Provider>
  );
};
